import {
    isMessagePayload,
    isDocumentPayload,
    BaseAuditEvent,
    AuditEvent,
    MessageAuditEvent,
    DocumentAuditEvent,
    ClassificationEventType,
    ClassificationEventName,
} from './auditEventTypes';
import type { AuthService } from '../auth/authService';
import { AppSettingsService } from '../appSettingsService';
import { TitusTokenService } from '../auth/titusTokenService';
import { LocalSettings, localSettingsService } from '../localSettingsService';
import { IEnvironmentAuditDataProvider } from './environmentAuditDataProvider';
import { IItemAuditDataProvider } from './auditDataProvider';
import { ActionTypes } from 'titus-ts/dist/js/Util/Actions/ActionTypes';
import { AuditObjectFilter } from './auditObjectFilter';
import { v4 as uuidv4 } from 'uuid';
import { actionSeveritytoAuditEventSuffixMapping, eventTypeToAuditKeyMap } from './policyKeyMappings';

export abstract class AuditService {
    static getEventTypeFromActionType(actionType: ActionTypes): ClassificationEventType {
        switch (actionType) {
            case 'RecipientAlert':
                return 'RecipientAlert';
            case 'ContentAlert':
                return 'Unknown';
            case 'ClassificationAlert':
                return 'Unknown';
            case 'Alert':
                return 'Alert';
            case 'BodyTag':
                return 'ApplyBodyTag';
            case 'SubjectLabel':
                return 'ApplySubjectLabel';
            case 'CustomXHeader':
                return 'ApplyCustomXHeader';
            case 'SetClassification':
                return 'SetClassification';
            case 'Deny':
                return 'Unknown';
            case 'HTMLHeaderFooter':
                return 'ApplyHtmlHeadersFooters';
            case 'AdvancedHeaderFooter':
                return 'ApplyAdvancedHeadersFooters';
            case 'SetCustomProperties':
                return 'ApplyCustomProperty';
            case 'CustomAction':
                return 'ApplyCustomAction';
            default:
                return 'Unknown';
        }
    }
    protected readonly authService: AuthService;
    protected readonly localSettings: LocalSettings;
    protected appSettingsService: AppSettingsService;
    protected environmentInfoProvider: IEnvironmentAuditDataProvider;
    protected auditDataProvider: IItemAuditDataProvider;
    protected auditObjectFilter: AuditObjectFilter;

    constructor(
        authService: AuthService,
        appSettingsService: AppSettingsService,
        environmentInfoProvider: IEnvironmentAuditDataProvider,
        auditDataProvider: IItemAuditDataProvider,
        auditObjectFilter: AuditObjectFilter,
    ) {
        this.authService = authService;
        this.appSettingsService = appSettingsService;
        this.environmentInfoProvider = environmentInfoProvider;
        this.auditDataProvider = auditDataProvider;
        this.auditObjectFilter = auditObjectFilter;

        this.localSettings = localSettingsService.getLocalSettings();
    }

    public async auditAction(
        event: ClassificationEventName,
        actionType: ActionTypes,
        actionSeverity?: string,
    ): Promise<void> {
        const eventType = AuditService.getEventTypeFromActionType(actionType);

        if (await this.shouldLogEvent(eventType, actionSeverity)) {
            await this.sendAudit(event, eventType);
        }
    }

    public async auditEventBlocked(event: ClassificationEventName): Promise<void> {
        let eventType: ClassificationEventType;

        if (event == 'OnSend') {
            eventType = 'EmailSendPrevented';
        } else if (event == 'OnClassificationChange' || event == 'OnDocClassificationChange') {
            eventType = 'ChangePrevented';
        } else {
            return;
        }

        if (await this.shouldLogEvent(eventType)) {
            await this.sendAudit(event, eventType);
        }
    }

    public async auditEventAllowed(event: ClassificationEventName, policyIgnored: boolean): Promise<void> {
        let eventType: ClassificationEventType;

        if (event == 'OnSend') {
            if (policyIgnored) {
                eventType = 'EmailSendAnyway';
            } else {
                eventType = 'EmailSent';
            }
        } else if (event == 'OnClassificationChange' || event == 'OnDocClassificationChange') {
            eventType = 'ChangeAllowed';
        } else {
            return;
        }

        if (await this.shouldLogEvent(eventType)) {
            await this.sendAudit(event, eventType);
        }
    }

    public async auditMessageSend(userActionRequired: boolean, sendAnyway: boolean): Promise<void> {
        const event = 'OnSend' as ClassificationEventName;
        let eventType: ClassificationEventType;

        if (userActionRequired && !sendAnyway) {
            eventType = 'EmailSendPrevented';
        } else if (userActionRequired && sendAnyway) {
            eventType = 'EmailSendAnyway';
        } else {
            eventType = 'EmailSent';
        }

        if (await this.shouldLogEvent(eventType)) {
            await this.sendAudit(event, eventType);
        }
    }

    private async buildAuditEvent(
        event: ClassificationEventName,
        eventType: ClassificationEventType,
    ): Promise<AuditEvent> {
        const token = await this.authService.getCachedTitusToken();
        const tenantId = TitusTokenService.getTitusTenantIdFromToken(token) || '';

        const base: BaseAuditEvent = {
            id: uuidv4(),
            TenantId: tenantId,
            UtcDateTime: new Date().toISOString(),
            Environment: this.environmentInfoProvider.getEnvironmentInfo(),
        };
        const itemAuditData = await this.auditDataProvider.getItemAuditProperties(event, eventType);

        if (isMessagePayload(itemAuditData)) {
            return { ...base, MessageEvent: itemAuditData } as MessageAuditEvent;
        }
        if (isDocumentPayload(itemAuditData)) {
            return { ...base, DocumentEvent: itemAuditData } as DocumentAuditEvent;
        }

        throw new Error(`Unsupported event payload: ${JSON.stringify(itemAuditData)}`);
    }

    private async sendAudit(event: ClassificationEventName, eventType: ClassificationEventType): Promise<boolean> {
        // Build the audit event
        const auditEvent = await this.buildAuditEvent(event, eventType);

        // Filter the audit object based on the policy
        const payload = this.auditObjectFilter.toJSON(auditEvent);

        // Allow specific implementation to submit the event to support different collecter types
        return await this.submitEvent(payload);
    }

    private async shouldLogEvent(eventType: ClassificationEventType, actionSeverity?: string): Promise<boolean> {
        return (await this.isSupportedLoggerEnabled()) && (await this.isEventTypeEnabled(eventType, actionSeverity));
    }

    private async isSupportedLoggerEnabled(): Promise<boolean> {
        const enabledLoggers = (await this.appSettingsService?.getApplicationSettings())?.AuditLogSeverity
            ?.EnabledLoggers;

        if (enabledLoggers?.includes('Fortra DLP Analytics and Reporting')) {
            return true;
        }

        return false;
    }

    private async isEventTypeEnabled(eventType: ClassificationEventType, actionSeverity?: string): Promise<boolean> {
        const auditEventWhitelist = (await this.appSettingsService?.getApplicationSettings())?.AuditFilter?.[
            'Audit Events'
        ];
        if (!auditEventWhitelist) return false;

        // Get the policy manager key that would be used for this eventType then see if its in the whitelist
        const auditEventKey = eventTypeToAuditKeyMap[eventType];
        if (auditEventKey == '__UNKNOWN__') return false;
        if (auditEventWhitelist.includes(auditEventKey)) return true;

        // Some events have variants for the action severity
        if (eventType === 'Alert' || eventType === 'RecipientAlert' || eventType === 'SetClassification') {
            // use the fixed mapping to convert the action severity to the key suffix used in the audit event Whitelist
            const whitelistSeveritySuffix = actionSeverity
                ? actionSeveritytoAuditEventSuffixMapping[actionSeverity]
                : undefined;

            if (whitelistSeveritySuffix && auditEventWhitelist.includes(`${auditEventKey}${whitelistSeveritySuffix}`)) {
                return true;
            }
        }

        return false;
    }

    protected abstract submitEvent(payload: string): Promise<boolean>;
}
